import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { faFileDownload } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, Button, LinearProgress, Typography } from '@mui/material';

import { claimQueries } from '@repo/api/claim/claims';
import { EIntervalType } from '@repo/api/claim/claims.types';
import { HTTPError } from '@repo/api/client';
import { downloadExtractSalesLoss } from '@repo/api/shared/generics';
import { useUser } from '@repo/auth';
import useCurrency from '@repo/features/hooks/useCurrency';
import { downloadFile } from '@repo/ui/utils/download';

import { ErrorMessage } from '../-components/ErrorMessage';
import { DailySalesLossTable } from './-components/DailySalesLossTable';
import { MonthlySalesLossTable } from './-components/MonthlySalesLossTable';
import { SalesAnalysisFootnotes } from './-components/SalesAnalysisFootnotes';
import { WeeklySalesLossTable } from './-components/WeeklySalesLossTable';

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const getErrorMessage = (error: unknown) => {
	if (error instanceof HTTPError) {
		if (typeof error.data === 'string' && error.data.trim()) {
			return error.data;
		}

		if (isRecord(error.data)) {
			const generalError = error.data.generalError;
			if (
				isRecord(generalError) &&
				typeof generalError.errorMessage === 'string' &&
				generalError.errorMessage.trim()
			) {
				return generalError.errorMessage;
			}

			const propertyErrors = error.data.propertyErrors;
			if (Array.isArray(propertyErrors)) {
				for (const propertyError of propertyErrors) {
					if (
						isRecord(propertyError) &&
						typeof propertyError.errorMessage === 'string' &&
						propertyError.errorMessage.trim()
					) {
						return propertyError.errorMessage;
					}
				}
			}
		}
	}

	if (error instanceof Error && error.message.trim()) {
		return error.message;
	}

	return 'Unexpected error';
};

export const Route = createFileRoute(
	'/_claims/claim/$claimId/_claim/calculations/salesLoss',
)({
	component: function SalesLossCalculationsPage() {
		const { claimId } = Route.useParams();
		const { data: claim } = useSuspenseQuery(claimQueries.id(claimId));
		const {
			data: { salesPeriodData } = {},
			isPending,
			error,
		} = useQuery(claimQueries.salesLoss(claimId));
		const {
			data: salesAnalysisData,
			isPending: isSalesAnalysisPending,
			error: salesAnalysisError,
		} = useQuery(claimQueries.salesAnalysis(claimId));
		const { permissions } = useUser();
		const canExtractSalesLoss = permissions.includes(
			'claim.sales.loss.extract',
		);
		const currency = useCurrency(claim.claimInsured.countryId);

		if (isPending || isSalesAnalysisPending) {
			return <LinearProgress />;
		}

		const queryError = error ?? salesAnalysisError;
		if (queryError) {
			return <ErrorMessage message={getErrorMessage(queryError)} />;
		}

		if (!salesAnalysisData) {
			return <ErrorMessage message="Unexpected error" />;
		}

		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 3,
					width: '1100px',
					maxWidth: '100%',
					mx: 'auto',
					py: 2,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 2,
						flexWrap: 'wrap',
					}}
				>
					<Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
						Sales Loss
					</Typography>
					{canExtractSalesLoss && (
						<Button
							variant="outlined"
							color="button"
							startIcon={<FontAwesomeIcon icon={faFileDownload} />}
							onClick={() =>
								downloadExtractSalesLoss(
									claimId,
									claim.claimReferenceNumber,
								).then(downloadFile)
							}
						>
							Extract Sales Loss
						</Button>
					)}
				</Box>

				{salesPeriodData?.intervalType === EIntervalType.Daily && (
					<DailySalesLossTable data={salesPeriodData} currency={currency} />
				)}
				{salesPeriodData?.intervalType === EIntervalType.Weekly && (
					<WeeklySalesLossTable data={salesPeriodData} currency={currency} />
				)}
				{salesPeriodData?.intervalType === EIntervalType.Monthly && (
					<MonthlySalesLossTable data={salesPeriodData} currency={currency} />
				)}
				<SalesAnalysisFootnotes data={salesAnalysisData.salesPeriodData} />
			</Box>
		);
	},
});
